import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Field, FieldProps } from "formik";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { datePeriod as defaultDatePeriod } from "constants/bookkeeping";
import { getDateRangeWithSetting, DateRangeValue } from "utils/getDateRange";
import { getDatePeriodWithSetting } from "utils/getDatePeriod";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { SingleValue, MultiValue } from "react-select";
import { OptionData } from "components/DesignSystem/Combobox/Combobox";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";

type DateStepValues = {
  start_date: string;
  end_date: string;
};

type SmartReviewDateStepProps = {
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (values: DateStepValues) => void;
};

const CUSTOM_PERIOD = "CUSTOM";

export const SmartReviewDateStep = ({
  isLoading,
  onClose,
  onSubmit,
}: SmartReviewDateStepProps) => {
  const today = new Date();
  const defaultStartDate = dayjs()
    .subtract(1, "month")
    .startOf("month")
    .format(YYYY_MM_DD);
  const defaultEndDate = dayjs()
    .subtract(1, "month")
    .endOf("month")
    .format(YYYY_MM_DD);

  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const periodOptions = getDatePeriodWithSetting(
    [...defaultDatePeriod, { label: "Custom", value: CUSTOM_PERIOD }],
    undefined,
    bookkeepingStartDate
  );

  const effectiveMinStartDate = bookkeepingStartDate
    ? new Date(bookkeepingStartDate)
    : undefined;

  const handleSubmit = ({ start_date, end_date }: typeof initialValues) =>
    onSubmit({ start_date, end_date });

  const initialValues = {
    selected_period: "lastmonth",
    start_date: defaultStartDate,
    end_date: defaultEndDate,
  };

  return (
    <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue }) => {
        const handlePeriodChange = (
          option: SingleValue<OptionData> | MultiValue<OptionData>
        ) => {
          if (!option || option instanceof Array) return;
          setFieldValue("selected_period", option.value);

          if (option.value === CUSTOM_PERIOD) return;

          const { startDate, endDate } = getDateRangeWithSetting(
            option.value as DateRangeValue,
            undefined,
            bookkeepingStartDate
          );
          setFieldValue("start_date", startDate.format(YYYY_MM_DD));
          setFieldValue("end_date", endDate.format(YYYY_MM_DD));
        };

        const handleDateChange =
          (field: "start_date" | "end_date") => (date: Date) => {
            setFieldValue("selected_period", CUSTOM_PERIOD);
            setFieldValue(field, dayjs(date).format(YYYY_MM_DD));
          };

        return (
          <>
            <Modal.Body className="sm:t-px-4">
              <p className="t-text-body-sm t-text-text-30 t-mb-5">
                We review your AI-categorised transactions from the selected
                period and show you only the ones we're sure about. Review &
                confirm them all in one click.
              </p>
              <div className="t-flex t-flex-col t-gap-4">
                <Combobox
                  name="selected_period"
                  label="Select period"
                  required
                  options={periodOptions}
                  value={periodOptions.find(
                    (p) => p.value === values.selected_period
                  )}
                  onChange={handlePeriodChange}
                  placeholder="Select period"
                  isOptionDisabled={(option) => Boolean(option.disableOption)}
                  isClearable={false}
                  menuPortalTarget={document.body}
                />
                <div className="t-flex t-items-center t-gap-3 t-w-full sm:t-flex-col sm:t-items-stretch">
                  <div className="t-flex-1">
                    <Field name="start_date">
                      {({ field }: FieldProps) => (
                        <DateInput
                          {...field}
                          required
                          block
                          label="Start date"
                          placeholder="Start date"
                          portalId="smart-review-start-date"
                          minDate={effectiveMinStartDate}
                          maxDate={
                            values.end_date ? new Date(values.end_date) : today
                          }
                          onDateChange={handleDateChange("start_date")}
                        />
                      )}
                    </Field>
                  </div>
                  <span className="t-text-text-30 t-mt-5 sm:t-hidden">→</span>
                  <div className="t-flex-1">
                    <Field name="end_date">
                      {({ field }: FieldProps) => (
                        <DateInput
                          {...field}
                          required
                          block
                          label="End date"
                          placeholder="End date"
                          portalId="smart-review-end-date"
                          minDate={
                            values.start_date
                              ? new Date(values.start_date)
                              : effectiveMinStartDate
                          }
                          maxDate={today}
                          onDateChange={handleDateChange("end_date")}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="sm:t-px-4">
              <div className="t-flex t-justify-end t-gap-3">
                <Button customType="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading || !values.start_date || !values.end_date}
                  type="submit"
                >
                  Next
                </Button>
              </div>
            </Modal.Footer>
          </>
        );
      }}
    </FormikForm>
  );
};
